import { ReactNode, useCallback, useMemo, useState } from 'react';

import { api } from '@/api';
import { useUser } from '@/core/user';
import { createJiraSubmitter } from '@/shared/util';

import { ReportIssueHandlerContext } from './report-issue-handler.context';
import {
  AboutType,
  ISSUE_TYPE,
  IssueType,
  ReportIssueHandlerState,
} from './report-issue-handler.model';
import { getShortAboutType } from './report-issue-handler.util';

interface ReportIssueHandlerProviderProps {
  children: ReactNode;
}

export function ReportIssueHandlerProvider({ children }: ReportIssueHandlerProviderProps) {
  const [issueType, setIssueType] = useState<IssueType>();
  const [aboutType, setAboutType] = useState<AboutType>();
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  const { user } = useUser();

  const {
    isError: isReportIssueError,
    isPending: isReportIssuePending,
    isSuccess: isReportIssueSuccess,
    mutate: reportIssue,
    reset: resetReportIssue,
  } = api.fct.reportIssue.mutation.usePostReportIssue();

  const {
    isError: isReportServiceDeskIssueError,
    isPending: isReportServiceDeskIssuePending,
    isSuccess: isReportServiceDeskIssueSuccess,
    mutate: reportServiceDeskIssue,
    reset: resetReportServiceDeskIssue,
  } = api.fct.reportServiceDeskIssue.mutation.usePostReportServiceDeskIssue();

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter((file) => {
      const isValidType = ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024;

      return isValidType && isValidSize;
    });

    setAttachedFiles((prev) => [...prev, ...validFiles]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetForm = useCallback(() => {
    setIssueType(undefined);
    setAboutType(undefined);
    setTitle('');
    setDescription('');
    setAttachedFiles([]);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!title || !issueType) {
      setShowErrors(true);

      return;
    }

    const jiraSubmitter = createJiraSubmitter({
      email: user.email,
      name: user.fullname,
      prenuvoID: user.prenuvoID,
    });

    const postData = jiraSubmitter({
      description,
      issueTypeName: issueType === ISSUE_TYPE.REPORT_ISSUE ? 'Bug' : 'Story',
      projectKey: 'FC',
      title: `${aboutType ? getShortAboutType(aboutType) : ''} ${title}`,
    });

    if (issueType !== ISSUE_TYPE.REPORT_ISSUE) {
      reportIssue(
        { payload: postData },
        {
          onSuccess: () => {
            resetForm();
          },
        },
      );
    } else {
      reportServiceDeskIssue(
        {
          payload: {
            description: postData.description,
            email: user.email,
            issuetype: postData.issuetype,
            project: postData.project,
            summary: postData.summary,
          },
        },
        {
          onSuccess: () => {
            resetForm();
          },
        },
      );
    }
  }, [
    aboutType,
    description,
    issueType,
    reportIssue,
    reportServiceDeskIssue,
    resetForm,
    title,
    user,
  ]);

  const value: ReportIssueHandlerState = useMemo(
    () => ({
      aboutType,
      attachedFiles,
      description,
      handleFileChange,
      handleSubmit,
      isReportIssueError,
      isReportIssuePending,
      isReportIssueSuccess,
      isReportServiceDeskIssueError,
      isReportServiceDeskIssuePending,
      isReportServiceDeskIssueSuccess,
      issueType,
      removeFile,
      resetForm,
      resetReportIssue,
      resetReportServiceDeskIssue,
      setAboutType,
      setDescription,
      setIssueType,
      setShowErrors,
      setTitle,
      showErrors,
      title,
    }),
    [
      aboutType,
      attachedFiles,
      description,
      handleFileChange,
      handleSubmit,
      isReportIssueError,
      isReportIssuePending,
      isReportIssueSuccess,
      isReportServiceDeskIssueError,
      isReportServiceDeskIssuePending,
      isReportServiceDeskIssueSuccess,
      issueType,
      removeFile,
      resetForm,
      resetReportIssue,
      resetReportServiceDeskIssue,
      setAboutType,
      setDescription,
      setIssueType,
      setShowErrors,
      setTitle,
      showErrors,
      title,
    ],
  );

  return <ReportIssueHandlerContext value={value}>{children}</ReportIssueHandlerContext>;
}
